import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { FaSort, FaSearch, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";
import { usePersonnel } from "../../hooks/usePersonnel";
import { useUnits } from "../../hooks/useUnits";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useVirtualScroll } from "../../hooks/useVirtualScroll";
import Modal from "../../components/forms/Modal";
import PersonnelForm from "../../components/forms/PersonnelForm";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import "./Personnel.css";
import { formatPersonDisplayName } from "../../utils/personnelDisplay";

// Constants
const DEFAULT_ITEMS_PER_PAGE = 50;
const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100, 200];
const SEARCH_DEBOUNCE_DELAY = 300;
/** Below this count, mobile renders all cards (document scroll). Above, fixed viewport + virtual list. */
const PERSONNEL_MOBILE_VIRTUAL_THRESHOLD = 40;
const PERSONNEL_MOBILE_CARD_GAP = 14;
const DEFAULT_PERSONNEL_MOBILE_CARD_HEIGHT = 200;

// Table columns configuration
const TABLE_COLUMNS = [
  { key: "personalNumber", label: "מספר אישי" },
  { key: "firstName", label: "שם פרטי" },
  { key: "lastName", label: "שם משפחה" },
  { key: "nickname", label: "כינוי" },
  { key: "phone", label: "טלפון" },
  { key: "rank", label: "דרגה" },
  { key: "isAttached", label: "מסופח", noSort: true },
  { key: "unitId", label: "יחידה" },
  { key: "isSecret", label: "משקש", noSort: true },
  { key: "actions", label: "פעילות", noSort: true },
];

const PersonnelMobileCard = React.memo(function PersonnelMobileCard({
  person,
  unitName,
  canManagePersonnel,
  operationLoading,
  onEdit,
  onDelete,
}) {
  const pid = person.id || person.personnelId;
  return (
    <div className="personnel-mobile-card">
      <div className="personnel-mobile-card-header">
        <div className="personnel-mobile-card-title">
          <span className="personnel-mobile-name">
            {formatPersonDisplayName(person) || "ללא שם"}
          </span>
          <span className="personnel-mobile-ma" aria-label="מספר אישי">
            · מ.א. {person.personalNumber ?? "—"}
          </span>
        </div>
      </div>
      <div className="personnel-mobile-card-grid">
        <div className="personnel-mobile-field">
          <span className="personnel-mobile-label">כינוי</span>
          <span className="personnel-mobile-value">
            {(person.nickname || "").trim() || "—"}
          </span>
        </div>
        <div className="personnel-mobile-field">
          <span className="personnel-mobile-label">טלפון</span>
          <span className="personnel-mobile-value">
            {person.phone || "לא זמין"}
          </span>
        </div>
        <div className="personnel-mobile-field">
          <span className="personnel-mobile-label">דרגה</span>
          <span className="personnel-mobile-value">
            {person.rank || "לא זמין"}
          </span>
        </div>
        <div className="personnel-mobile-field">
          <span className="personnel-mobile-label">יחידה</span>
          <span className="personnel-mobile-value">{unitName}</span>
        </div>
        <div className="personnel-mobile-badges-row">
          <span
            className={`status-badge ${
              person.isAttached ? "status-attached" : "status-not-attached"
            }`}
          >
            מסופח: {person.isAttached ? "כן" : "לא"}
          </span>
          <span
            className={`status-badge ${
              person.isSecret ? "status-secret" : "status-not-secret"
            }`}
          >
            משקש: {person.isSecret ? "כן" : "לא"}
          </span>
        </div>
      </div>
      <div className="personnel-mobile-card-actions">
        {canManagePersonnel ? (
          <>
            <button
              type="button"
              className="personnel-mobile-action-btn edit-btn"
              onClick={() => onEdit(person)}
              disabled={operationLoading}
            >
              <FaEdit /> ערוך
            </button>
            <button
              type="button"
              className="personnel-mobile-action-btn delete-btn"
              onClick={() => onDelete(pid)}
              disabled={operationLoading}
            >
              <FaTrash /> מחק
            </button>
          </>
        ) : (
          <span className="view-only">צפייה בלבד</span>
        )}
      </div>
    </div>
  );
});

const Personnel = () => {
  const isMobile = useIsMobile();
  const { user, isAdmin } = useAuth();
  const { units } = useUnits();
  const {
    personnel,
    loading,
    error,
    addPersonnel,
    updatePersonnel,
    deletePersonnel,
  } = usePersonnel();

  // Permissions
  const canManagePersonnel = isAdmin; // Only admin can edit/delete
  const canAddPersonnel = isAdmin || user?.permissions === "childUser"; // Admin and childUser can add
  const canViewPersonnel =
    isAdmin ||
    user?.permissions === "parentsUser" ||
    user?.permissions === "childUser";

  // Get childUser's unitId for filtering
  const childUserUnitId = useMemo(() => {
    if (user?.permissions !== "childUser") return null;

    const employeeId = user?.employeeId;
    if (!employeeId) return null;

    // Find personnel record by personalNumber (employeeId is the personalNumber)
    const currentUserPersonnel = personnel.find(
      (p) => p.personalNumber === employeeId
    );
    return currentUserPersonnel?.unitId || null;
  }, [user, personnel]);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState("");
  const [sortField, setSortField] = useState("personalNumber");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  // Debounced search optimization
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized unit lookup map for O(1) access
  const unitMap = useMemo(() => {
    const map = new Map();
    units.forEach((unit) => {
      map.set(unit.unitId, unit.unitName);
    });
    return map;
  }, [units]);

  // Optimized data processing with memoization
  const filteredPersonnel = useMemo(() => {
    if (!personnel.length) return [];

    let filtered = personnel;

    // For childUser, filter to only show employees from the same unit
    if (user?.permissions === "childUser" && childUserUnitId) {
      filtered = filtered.filter((person) => person.unitId === childUserUnitId);
    }

    // Optimized search with single string comparison
    if (debouncedSearchTerm.trim()) {
      const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((person) => {
        const searchText = [
          person.personalNumber?.toString() || "",
          person.firstName?.toLowerCase() || "",
          person.lastName?.toLowerCase() || "",
          (person.nickname || "").toLowerCase(),
          person.phone?.toLowerCase() || "",
          person.rank?.toLowerCase() || "",
          unitMap.get(person.unitId)?.toLowerCase() || "",
        ].join(" ");

        return searchText.includes(lowerSearchTerm);
      });
    }

    // Optimized sorting with stable comparison
    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "unitId") {
        aValue = unitMap.get(a.unitId);
        bValue = unitMap.get(b.unitId);
      }

      aValue = aValue ?? "";
      bValue = bValue ?? "";

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      const result = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      return sortDirection === "asc" ? result : -result;
    });
  }, [
    personnel,
    debouncedSearchTerm,
    unitMap,
    sortField,
    sortDirection,
    user?.permissions,
    childUserUnitId,
  ]);

  // Pagination calculations with memoization
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPersonnel = filteredPersonnel.slice(startIndex, endIndex);

    return {
      totalPages,
      startIndex,
      endIndex,
      paginatedPersonnel,
      totalItems: filteredPersonnel.length,
    };
  }, [filteredPersonnel, currentPage, itemsPerPage]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, sortField, sortDirection]);

  // Optimized event handlers
  const handleEdit = useCallback((person) => {
    setEditingPersonnel(person);
  }, []);

  const handleAddPersonnel = useCallback(
    async (formData) => {
      setOperationLoading(true);
      setOperationError("");

      const result = await addPersonnel({
        ...formData,
        nickname: (formData.nickname || "").trim(),
      });
      if (result.success) {
        setShowAddModal(false);
      } else {
        setOperationError(result.error);
      }
      setOperationLoading(false);
    },
    [addPersonnel]
  );

  const handleUpdatePersonnel = useCallback(
    async (formData) => {
      setOperationLoading(true);
      setOperationError("");

      const result = await updatePersonnel(
        editingPersonnel.id || editingPersonnel.personnelId,
        {
          ...formData,
          nickname: (formData.nickname || "").trim(),
        }
      );
      if (result.success) {
        setEditingPersonnel(null);
      } else {
        setOperationError(result.error);
      }
      setOperationLoading(false);
    },
    [updatePersonnel, editingPersonnel]
  );

  const handleDeletePersonnel = useCallback(
    async (personnelId) => {
      if (
        window.confirm(
          "האם אתה בטוח שברצונך למחוק חייל זה?\n\nפעולה זו לא ניתנת לביטול!"
        )
      ) {
        setOperationLoading(true);
        setOperationError("");

        const result = await deletePersonnel(personnelId);
        if (!result.success) {
          setOperationError(result.error);
        }
        setOperationLoading(false);
      }
    },
    [deletePersonnel]
  );

  const handleCloseModal = useCallback(() => {
    setEditingPersonnel(null);
    setShowAddModal(false);
    setOperationError("");
  }, []);

  const handleSort = useCallback(
    (field) => {
      setSortField(field);
      setSortDirection((prev) =>
        sortField === field && prev === "asc" ? "desc" : "asc"
      );
    },
    [sortField]
  );

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const sortFieldOptions = useMemo(
    () => TABLE_COLUMNS.filter((c) => !c.noSort),
    []
  );

  const shouldUsePersonnelMobileVirtual = useMemo(
    () =>
      isMobile &&
      filteredPersonnel.length >= PERSONNEL_MOBILE_VIRTUAL_THRESHOLD,
    [isMobile, filteredPersonnel.length]
  );

  const mobileContainerRef = useRef(null);
  const mobileCardHeightRef = useRef(null);

  const mobileContainerHeight = useMemo(() => {
    if (!isMobile || typeof window === "undefined") return 500;
    const reserved = 280;
    const available = window.innerHeight - reserved;
    return Math.max(320, Math.min(available, 900));
  }, [isMobile]);

  const [mobileCardHeight, setMobileCardHeight] = useState(
    DEFAULT_PERSONNEL_MOBILE_CARD_HEIGHT
  );

  const personnelVirtualScrollItems = useMemo(
    () => (shouldUsePersonnelMobileVirtual ? filteredPersonnel : []),
    [shouldUsePersonnelMobileVirtual, filteredPersonnel]
  );

  const {
    visibleItems: mobileVisiblePersonnel,
    totalHeight: mobileTotalHeight,
    offsetY: mobileOffsetY,
    setContainerRef: setMobileVirtualContainerRef,
  } = useVirtualScroll(
    personnelVirtualScrollItems,
    mobileCardHeight,
    mobileContainerHeight
  );

  const setMobileContainerCallback = useCallback(
    (node) => {
      mobileContainerRef.current = node;
      if (node && isMobile && shouldUsePersonnelMobileVirtual) {
        setMobileVirtualContainerRef(node);
      } else {
        setMobileVirtualContainerRef(null);
      }
    },
    [isMobile, shouldUsePersonnelMobileVirtual, setMobileVirtualContainerRef]
  );

  useEffect(() => {
    if (shouldUsePersonnelMobileVirtual) {
      setMobileCardHeight(DEFAULT_PERSONNEL_MOBILE_CARD_HEIGHT);
    }
  }, [shouldUsePersonnelMobileVirtual]);

  useEffect(() => {
    if (
      !isMobile ||
      !shouldUsePersonnelMobileVirtual ||
      !mobileCardHeightRef.current
    ) {
      return;
    }

    const measureCardHeight = () => {
      const wrap = mobileCardHeightRef.current;
      if (wrap?.firstChild) {
        const card = wrap.firstChild;
        const measured = card.offsetHeight || wrap.offsetHeight;
        if (measured > 0) {
          setMobileCardHeight(measured + PERSONNEL_MOBILE_CARD_GAP);
        }
      }
    };

    const t = setTimeout(measureCardHeight, 150);
    const onResize = () => setTimeout(measureCardHeight, 100);
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [
    isMobile,
    shouldUsePersonnelMobileVirtual,
    filteredPersonnel.length,
    mobileVisiblePersonnel.length,
  ]);

  // Early returns for better performance
  if (!canViewPersonnel) {
    return null;
  }

  if (loading) {
    return (
      <div
        id="personnel-page-scope"
        className="personnel-management-container"
      >
        <LoadingSpinner message="טוען חיילים..." />
      </div>
    );
  }

  return (
    <div
      id="personnel-page-scope"
      className="personnel-management-container"
    >
      {error && <ErrorMessage message={error} />}
      {operationError && (
        <ErrorMessage
          message={operationError}
          onDismiss={() => setOperationError("")}
        />
      )}

      <div className="personnel-management-header">
        <h1>ניהול חיילים</h1>
        {canAddPersonnel && (
          <button
            className="add-personnel-btn"
            onClick={() => setShowAddModal(true)}
            disabled={operationLoading}
          >
            <FaPlus />
            הוסף חייל
          </button>
        )}
      </div>

      <div className="search-container">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="חפש בכל השדות..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {isMobile && (
        <div className="personnel-mobile-sort">
          <label htmlFor="personnel-mobile-sort-field">מיון:</label>
          <select
            id="personnel-mobile-sort-field"
            value={sortField}
            onChange={(e) => handleSort(e.target.value)}
            className="personnel-mobile-sort-select"
          >
            {sortFieldOptions.map((col) => (
              <option key={col.key} value={col.key}>
                {col.label}{" "}
                {sortField === col.key
                  ? sortDirection === "asc"
                    ? "↑"
                    : "↓"
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {isMobile ? (
        filteredPersonnel.length === 0 ? (
          <div className="personnel-mobile-empty">
            {searchTerm
              ? "לא נמצאו חיילים התואמים לחיפוש"
              : "אין חיילים במערכת"}
          </div>
        ) : shouldUsePersonnelMobileVirtual ? (
          <>
            <div
              ref={setMobileContainerCallback}
              className="personnel-mobile-scroll"
              style={{
                height: mobileContainerHeight,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <div
                style={{
                  height: mobileTotalHeight,
                  position: "relative",
                }}
              >
                <div
                  className="personnel-mobile-cards-inner"
                  style={{
                    transform: `translateY(${mobileOffsetY}px)`,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                  }}
                >
                  {mobileVisiblePersonnel.map((person, index) => (
                    <div
                      key={person.id || person.personnelId}
                      ref={
                        index === 0 && mobileVisiblePersonnel.length > 0
                          ? mobileCardHeightRef
                          : null
                      }
                      className="personnel-mobile-card-slot"
                      style={{
                        height: mobileCardHeight,
                        boxSizing: "border-box",
                      }}
                    >
                      <PersonnelMobileCard
                        person={person}
                        unitName={
                          unitMap.get(person.unitId) || "לא זמין"
                        }
                        canManagePersonnel={canManagePersonnel}
                        operationLoading={operationLoading}
                        onEdit={handleEdit}
                        onDelete={handleDeletePersonnel}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="personnel-mobile-count" aria-live="polite">
              סה&quot;כ {filteredPersonnel.length} חיילים
            </p>
          </>
        ) : (
          <div className="personnel-mobile-cards">
            {filteredPersonnel.map((person) => (
              <PersonnelMobileCard
                key={person.id || person.personnelId}
                person={person}
                unitName={unitMap.get(person.unitId) || "לא זמין"}
                canManagePersonnel={canManagePersonnel}
                operationLoading={operationLoading}
                onEdit={handleEdit}
                onDelete={handleDeletePersonnel}
              />
            ))}
            <p className="personnel-mobile-count" aria-live="polite">
              סה&quot;כ {filteredPersonnel.length} חיילים
            </p>
          </div>
        )
      ) : (
        <>
          <div className="personnel-table-container">
            <table className="personnel-table">
              <thead>
                <tr>
                  {TABLE_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      onClick={
                        column.noSort
                          ? undefined
                          : () => handleSort(column.key)
                      }
                      style={{
                        cursor: column.noSort ? "default" : "pointer",
                      }}
                    >
                      {!column.noSort && <FaSort />}
                      {column.label}
                      {!column.noSort && sortField === column.key && (
                        <span style={{ marginRight: "5px" }}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginationData.paginatedPersonnel.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      {searchTerm
                        ? "לא נמצאו חיילים התואמים לחיפוש"
                        : "אין חיילים במערכת"}
                    </td>
                  </tr>
                ) : (
                  paginationData.paginatedPersonnel.map((person) => (
                    <tr key={person.id || person.personnelId}>
                      <td>{person.personalNumber || "לא זמין"}</td>
                      <td>{person.firstName || "לא זמין"}</td>
                      <td>{person.lastName || "לא זמין"}</td>
                      <td>{(person.nickname || "").trim() || "—"}</td>
                      <td>{person.phone || "לא זמין"}</td>
                      <td>{person.rank || "לא זמין"}</td>
                      <td>
                        <span
                          className={`status-badge ${
                            person.isAttached
                              ? "status-attached"
                              : "status-not-attached"
                          }`}
                        >
                          {person.isAttached ? "כן" : "לא"}
                        </span>
                      </td>
                      <td>{unitMap.get(person.unitId) || "לא זמין"}</td>
                      <td>
                        <span
                          className={`status-badge ${
                            person.isSecret
                              ? "status-secret"
                              : "status-not-secret"
                          }`}
                        >
                          {person.isSecret ? "כן" : "לא"}
                        </span>
                      </td>
                      <td className="actions">
                        {canManagePersonnel ? (
                          <>
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(person)}
                              title="ערוך"
                              disabled={operationLoading}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() =>
                                handleDeletePersonnel(
                                  person.id || person.personnelId
                                )
                              }
                              title="מחק"
                              disabled={operationLoading}
                            >
                              <FaTrash />
                            </button>
                          </>
                        ) : (
                          <span className="view-only">צפייה בלבד</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {paginationData.totalItems > itemsPerPage && (
            <div className="pagination-container">
              <div className="pagination-info">
                <span>
                  מציג {paginationData.startIndex + 1}-
                  {Math.min(
                    paginationData.endIndex,
                    paginationData.totalItems
                  )}{" "}
                  מתוך {paginationData.totalItems} חיילים
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="items-per-page-select"
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} פריטים בעמוד
                    </option>
                  ))}
                </select>
              </div>

              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ראשון
                </button>
                <button
                  onClick={() =>
                    handlePageChange(Math.max(currentPage - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  הקודם
                </button>

                <span className="page-info">
                  עמוד {currentPage} מתוך {paginationData.totalPages}
                </span>

                <button
                  onClick={() =>
                    handlePageChange(
                      Math.min(currentPage + 1, paginationData.totalPages)
                    )
                  }
                  disabled={currentPage === paginationData.totalPages}
                  className="pagination-btn"
                >
                  הבא
                </button>
                <button
                  onClick={() => handlePageChange(paginationData.totalPages)}
                  disabled={currentPage === paginationData.totalPages}
                  className="pagination-btn"
                >
                  אחרון
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {canManagePersonnel && (
        <Modal
          isOpen={!!editingPersonnel}
          onClose={handleCloseModal}
          title="עריכת חייל"
        >
          <PersonnelForm
            initialValues={{
              personalNumber: editingPersonnel?.personalNumber || "",
              firstName: editingPersonnel?.firstName || "",
              lastName: editingPersonnel?.lastName || "",
              nickname: editingPersonnel?.nickname || "",
              phone: editingPersonnel?.phone || "",
              rank: editingPersonnel?.rank || "",
              unitId: editingPersonnel?.unitId || "",
              isAttached: editingPersonnel?.isAttached || false,
              isSecret: editingPersonnel?.isSecret || false,
            }}
            onSubmit={handleUpdatePersonnel}
            onCancel={handleCloseModal}
            submitText="שמור שינויים"
            isEditing={true}
            existingUnits={units}
          />
        </Modal>
      )}

      {canAddPersonnel && (
        <Modal
          isOpen={showAddModal}
          onClose={handleCloseModal}
          title="הוספת חייל חדש"
        >
          <PersonnelForm
            initialValues={{
              personalNumber: "",
              firstName: "",
              lastName: "",
              nickname: "",
              phone: "",
              rank: "",
              unitId: childUserUnitId || "", // Pre-fill with childUser's unit if applicable
              isAttached: false,
              isSecret: false,
            }}
            onSubmit={handleAddPersonnel}
            onCancel={handleCloseModal}
            submitText="הוסף חייל"
            isEditing={false}
            existingUnits={
              // For childUser, only show their unit; for admin, show all units
              user?.permissions === "childUser" && childUserUnitId
                ? units.filter((unit) => unit.unitId === childUserUnitId)
                : units
            }
          />
        </Modal>
      )}
    </div>
  );
};

export default Personnel;
